import { useEffect, useMemo, useState } from 'react';

import { Combobox } from './Combobox';
import {
    loadStoreItems,
    loadStores,
    searchItems,
    storeLabel,
    type PbsItem,
    type Store,
} from './data';
import { StorePicker } from './StorePicker';
import { newClientRequestId, submitReport } from './report';

interface Line {
    itemId: string;
    qty: number;
}

interface ReportFormProps {
    defaultStore: string;
    onStoreChange: (storeId: string) => void;
}

const MAX_QTY = 999;

export function ReportForm({ defaultStore, onStoreChange }: ReportFormProps) {
    const [stores, setStores] = useState<Store[]>([]);
    const [storeId, setStoreId] = useState(defaultStore);
    const [items, setItems] = useState<PbsItem[]>([]);
    const [itemsLoading, setItemsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [query, setQuery] = useState('');
    const [itemId, setItemId] = useState('');
    const [qty, setQty] = useState(1);
    const [lines, setLines] = useState<Line[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    // Stable across retries of the SAME batch (so a flaky-wifi retry replays
    // idempotently instead of double-counting), fresh for the next one.
    const [clientRequestId, setClientRequestId] = useState<string | null>(null);

    useEffect(() => {
        loadStores()
            .then(setStores)
            .catch((err: unknown) =>
                setLoadError(err instanceof Error ? err.message : 'Could not load stores.'),
            );
    }, []);

    useEffect(() => {
        if (!storeId) {
            setItems([]);
            return;
        }
        setItemsLoading(true);
        loadStoreItems(storeId)
            .then((res) => {
                setItems(res.items);
                setLoadError(null);
            })
            .catch((err: unknown) =>
                setLoadError(err instanceof Error ? err.message : 'Could not load items.'),
            )
            .finally(() => setItemsLoading(false));
    }, [storeId]);

    const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
    const matches = useMemo(
        () => (query.trim() === '' ? items : searchItems(items, query)),
        [items, query],
    );
    const chosenItem = itemId ? itemsById.get(itemId) : undefined;
    const store = stores.find((s) => s.id === storeId);

    const totalUnits = lines.reduce((sum, l) => sum + l.qty, 0);
    const canAdd = storeId !== '' && itemId !== '' && qty >= 1;

    const chooseStore = (id: string) => {
        setStoreId(id);
        setItemId('');
        setQuery('');
        // Lines belong to the store they were counted at. Carrying them across a
        // store change meant a failed submit for store 595, a switch to 1004,
        // and a resubmit could replay the 595 rows as "accepted" (the server
        // resolved them against 595) while the rest landed on 1004 - one success
        // screen, one report split over two stores.
        setLines([]);
        setSubmitError(null);
        setClientRequestId(null);
        onStoreChange(id);
    };

    const addLine = () => {
        if (!canAdd) return;
        setLines((current) => {
            // Same item twice adds up rather than creating a second row, because
            // fact.RetailInventoryTransactions is unique on date + store + SKU.
            const at = current.findIndex((l) => l.itemId === itemId);
            if (at >= 0) {
                const next = [...current];
                next[at] = { ...next[at], qty: Math.min(MAX_QTY, next[at].qty + qty) };
                return next;
            }
            return [...current, { itemId, qty }];
        });
        setItemId('');
        setQuery('');
        setQty(1);
        // Editing the lines makes this a different report, so it needs a
        // different idempotency key. Holding the old one meant a corrected
        // resubmit was treated as a replay of the original batch: the server
        // returned the ORIGINAL quantities and the app cheerfully showed
        // "Report submitted" with the corrected ones on screen.
        setClientRequestId(null);
    };

    const removeLine = (id: string) => {
        setLines((current) => current.filter((l) => l.itemId !== id));
        setClientRequestId(null);
    };

    const startOver = () => {
        setLines([]);
        setItemId('');
        setQuery('');
        setQty(1);
        setSubmitted(false);
        setSubmitError(null);
        setClientRequestId(null);
    };

    const submitLines = async () => {
        if (lines.length === 0 || submitting) return;
        setSubmitError(null);
        setSubmitting(true);

        // Stable for as long as the lines are: an untouched retry after a failed
        // submit replays idempotently, but adding, removing or re-adding a line
        // clears this (see addLine/removeLine) so a corrected report is a new
        // batch rather than a replay of the old numbers.
        const requestId = clientRequestId ?? newClientRequestId();
        setClientRequestId(requestId);

        const result = await submitReport(storeId, lines, requestId);
        setSubmitting(false);

        if (result.ok) {
            setSubmitted(true);
        } else {
            // Lines are deliberately NOT cleared here - the whole point of
            // building this route was that the old submit button lied about
            // success. Losing the associate's work on top of a failed write
            // would be worse than the bug this fixes.
            setSubmitError(result.error);
        }
    };

    if (loadError) {
        return (
            <div className="content">
                <div className="alert alert-error">
                    <h3>Could not load stores and items</h3>
                    <p style={{ margin: 0 }}>{loadError}</p>
                </div>
            </div>
        );
    }

    if (stores.length === 0) {
        return (
            <div className="content">
                <p className="empty">Loading stores...</p>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="done">
                <div className="done-mark" aria-hidden="true">
                    &#10003;
                </div>
                <h2>Report submitted</h2>
                <p>
                    {totalUnits} {totalUnits === 1 ? 'plant' : 'plants'} from{' '}
                    {store ? storeLabel(store) : 'your store'}.
                </p>
                <button type="button" className="btn btn-primary" onClick={startOver}>
                    Report more plants
                </button>
            </div>
        );
    }

    return (
        <div className="content">
            <section className="step">
                <div className="step-head">
                    <span className="step-num">1</span>
                    <span className="step-title">Your store</span>
                </div>

                <StorePicker stores={stores} value={storeId} onChange={chooseStore} />
            </section>

            <section className="step">
                <div className="step-head">
                    <span className="step-num">2</span>
                    <span className="step-title">What was lost</span>
                </div>

                {storeId === '' ? (
                    <p className="hint">Choose your store first.</p>
                ) : itemsLoading ? (
                    <p className="empty">Loading your store&rsquo;s items...</p>
                ) : items.length === 0 ? (
                    <div className="alert">
                        <h3>No Pay-By-Scan plants found for store {storeId}</h3>
                        <p>
                            Nothing has been sold, received, or ordered at your store
                            recently, so there&rsquo;s nothing to report yet. If
                            you&rsquo;re holding a plant with a Pay-By-Scan tag, check
                            back in a day or two.
                        </p>
                    </div>
                ) : chosenItem ? (
                    <div className="chosen">
                        <span className="chosen-body">
                            <b>{chosenItem.desc}</b>
                            <span>Item {chosenItem.id}</span>
                        </span>
                        <button
                            type="button"
                            className="linkish"
                            onClick={() => {
                                setItemId('');
                                setQuery('');
                            }}
                        >
                            Change
                        </button>
                    </div>
                ) : (
                    <>
                        <Combobox
                            label="Search items"
                            placeholder="Search item number or description"
                            query={query}
                            onQueryChange={setQuery}
                            options={matches.map((m) => ({
                                id: m.id,
                                primary: m.desc,
                                secondary: `Item ${m.id}`,
                            }))}
                            onSelect={setItemId}
                            // Unlike the store field, this one opens its whole list
                            // on focus: a store carries ~77 items, which is worth
                            // browsing when you are holding a plant and unsure what
                            // its tag says.
                            openOnFocus
                            emptyHint={`Type part of the tag, e.g. "2 gal red", or an item number. ${items.length} items at your store.`}
                            noMatchHint="Nothing matches that. Try fewer words."
                        />
                    </>
                )}

                <div className="qty">
                    <button
                        type="button"
                        className="qty-btn"
                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                        disabled={qty <= 1}
                        aria-label="One fewer"
                    >
                        &minus;
                    </button>
                    <div className="qty-readout">
                        <div className="qty-value" aria-live="polite">
                            {qty}
                        </div>
                        <span className="qty-caption">Plants</span>
                    </div>
                    <button
                        type="button"
                        className="qty-btn"
                        onClick={() => setQty((q) => Math.min(MAX_QTY, q + 1))}
                        disabled={qty >= MAX_QTY}
                        aria-label="One more"
                    >
                        +
                    </button>
                </div>

                <button
                    type="button"
                    className="btn btn-secondary btn-add"
                    onClick={addLine}
                    disabled={!canAdd}
                >
                    Add to report
                </button>
            </section>

            <section className="step">
                <div className="step-head">
                    <span className="step-num">3</span>
                    <span className="step-title">
                        Review {lines.length > 0 ? `(${lines.length})` : ''}
                    </span>
                </div>

                {lines.length === 0 ? (
                    <p className="empty">Add a plant above and it shows up here.</p>
                ) : (
                    <ul className="list">
                        {lines.map((line) => {
                            const item = itemsById.get(line.itemId);
                            return (
                                <li className="list-row" key={line.itemId}>
                                    <span className="list-qty">{line.qty}</span>
                                    <span className="list-name">
                                        <b>{item?.desc ?? line.itemId}</b>
                                        <span>Item {line.itemId}</span>
                                    </span>
                                    <button
                                        type="button"
                                        className="list-remove"
                                        onClick={() => removeLine(line.itemId)}
                                        aria-label={`Remove item ${line.itemId}`}
                                    >
                                        &times;
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            {submitError && (
                <div className="alert alert-error" role="alert">
                    <h3>Could not submit</h3>
                    <p style={{ margin: 0 }}>{submitError}</p>
                </div>
            )}

            <button
                type="button"
                className="btn btn-primary"
                onClick={() => void submitLines()}
                disabled={lines.length === 0 || submitting}
            >
                {submitting
                    ? 'Submitting...'
                    : lines.length === 0
                      ? 'Submit report'
                      : `Submit ${totalUnits} ${totalUnits === 1 ? 'plant' : 'plants'}`}
            </button>
        </div>
    );
}
